import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const ShopPollsAdmin = () => {
  const [date, setDate] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = {};
      if (date) params.date = date;
      if (q.trim()) params.q = q.trim();
      const [s, l] = await Promise.all([
        axios.get(`${API_BASE_URL}/shop-polls/admin/stats`, { headers, params: date ? { date } : {} }),
        axios.get(`${API_BASE_URL}/shop-polls/admin`, { headers, params }),
      ]);
      setStats(s.data?.data || null);
      setRows(Array.isArray(l.data?.data) ? l.data.data : []);
      if (!date && l.data?.today) setDate(l.data.today);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Could not load polls');
    } finally {
      setLoading(false);
    }
  }, [date, q]);

  useEffect(() => {
    load();
  }, [load]);

  const today = stats?.today || {};
  const life = stats?.lifetime || {};

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <HowToVoteIcon color="primary" />
          <Typography variant="h5" fontWeight={800}>
            Shop polls
          </Typography>
        </Stack>
        <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Every Mitra can ask customers what to prepare or stock today (curries,
          items, timings — any question). Citizens vote and may add extra options.
          Use these counts to see which shops are running the flow.
        </Typography>
        <Button component={RouterLink} to="/admin-control-screens" size="small" sx={{ mb: 2 }}>
          ← Admin Control Screens
        </Button>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            type="date"
            size="small"
            label="Date (IST)"
            InputLabelProps={{ shrink: true }}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <TextField
            size="small"
            label="Search shop / question"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 240 }}
          />
          <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined">
            Refresh
          </Button>
        </Stack>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          {[
            ['Polls today', today.polls ?? 0],
            ['Open now', today.openPolls ?? 0],
            ['Votes today', today.votes ?? 0],
            ['Shops today', today.shops ?? 0],
            ['All-time votes', life.votes ?? 0],
            ['Customer-added options', life.citizenOptions ?? 0],
          ].map(([label, value]) => (
            <Paper key={label} sx={{ p: 1.5, flex: 1, minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="h5" fontWeight={800}>
                {value}
              </Typography>
            </Paper>
          ))}
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          Example: RK Curry Point asks “which curries today?”. Customers pick from
          Mitra options or type another dish. Mitra cooks from the live bars.
        </Alert>

        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          Leading options today
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
          {(stats?.topOptionsToday || []).length === 0 && (
            <Typography color="text.secondary">No votes on this date yet.</Typography>
          )}
          {(stats?.topOptionsToday || []).map((row, i) => (
            <Chip
              key={`${row.label}-${i}`}
              label={`${row.label} · ${row.votes} · ${row.businessName || ''}`}
              color="primary"
              variant="outlined"
            />
          ))}
        </Stack>

        <Paper>
          {loading && rows.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Shop</TableCell>
                  <TableCell>Question</TableCell>
                  <TableCell>Votes</TableCell>
                  <TableCell>Leading</TableCell>
                  <TableCell>Customer options</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((p) => {
                  const citizenOpts = (p.options || []).filter((o) => o.source === 'citizen').length;
                  return (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <Typography fontWeight={700}>{p.businessName || p.businessId}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.town || '—'} · {p.pollDate}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>{p.question}</TableCell>
                      <TableCell>{p.totalVotes || 0}</TableCell>
                      <TableCell>
                        {p.leadingOption
                          ? `${p.leadingOption.label} (${p.leadingOption.votes})`
                          : '—'}
                      </TableCell>
                      <TableCell>{citizenOpts}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={p.status}
                          color={p.status === 'open' ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No polls on this date.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Container>
    </DashboardLayout>
  );
};

export default ShopPollsAdmin;
